import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Send, Check, X } from 'lucide-react';
import {
  listSnippets,
  createSnippet,
  updateSnippet,
  removeSnippet,
  submitToStore,
  pendingReview,
  reviewSnippet,
  type NoteSnippet,
  type SnippetWithSubmitter,
  type CreateSnippetBody,
} from '@/lib/api/snippets.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { useAuthStore } from '@/stores/auth.js';

type Tab = 'my' | 'store';

const MANAGER_ROLES = new Set(['platform_admin', 'tenant_admin', 'store_manager']);

function isManager(role: string | undefined) {
  return Boolean(role && MANAGER_ROLES.has(role));
}

type FormValues = {
  triggerCode: string;
  title: string;
  content: string;
  category: string;
  level: 'personal' | 'store';
};

const EMPTY_FORM: FormValues = {
  triggerCode: '',
  title: '',
  content: '',
  category: '',
  level: 'personal',
};

function SnippetForm({
  initial,
  onSave,
  onCancel,
  saving,
  allowStore,
}: {
  initial?: FormValues;
  onSave: (values: FormValues) => void;
  onCancel: () => void;
  saving: boolean;
  allowStore: boolean;
}) {
  const [form, setForm] = useState<FormValues>(initial ?? EMPTY_FORM);
  const set = (key: keyof FormValues, value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  return (
    <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Trigger code</span>
          <div className="mt-1 flex items-center gap-1">
            <span className="text-slate-500">/</span>
            <input
              className="flex-1 rounded-xl border border-slate-300 px-3 py-2 font-mono text-sm outline-none focus:border-primary"
              placeholder="e.g. screen"
              value={form.triggerCode}
              onChange={(e) => set('triggerCode', e.target.value.replace(/^\//, '').toLowerCase().replace(/\s/g, '-'))}
              required
            />
          </div>
        </label>
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Title</span>
          <input
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary"
            placeholder="Display name"
            value={form.title}
            onChange={(e) => set('title', e.target.value)}
            required
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Category (optional)</span>
          <input
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary"
            placeholder="e.g. repairs, general"
            value={form.category}
            onChange={(e) => set('category', e.target.value)}
          />
        </label>
        {allowStore ? (
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Level</span>
            <select
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary"
              value={form.level}
              onChange={(e) => set('level', e.target.value as 'personal' | 'store')}
            >
              <option value="personal">Personal (just me)</option>
              <option value="store">Store (all staff)</option>
            </select>
          </label>
        ) : null}
      </div>
      <label className="block">
        <span className="text-sm font-medium text-slate-700">Content</span>
        <textarea
          className="mt-1 min-h-28 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary"
          placeholder="Text that gets inserted when the snippet is selected…"
          value={form.content}
          onChange={(e) => set('content', e.target.value)}
          required
        />
      </label>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={saving || !form.triggerCode.trim() || !form.title.trim() || !form.content.trim()}
          onClick={() => onSave(form)}
          className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 hover:bg-[#164477]"
        >
          {saving ? 'Saving…' : 'Save Snippet'}
        </button>
      </div>
    </div>
  );
}

function SnippetCard({
  snippet,
  currentUserId,
  userRole,
  onEdit,
  onDelete,
  onSubmitToStore,
}: {
  snippet: NoteSnippet;
  currentUserId: string | undefined;
  userRole: string | undefined;
  onEdit: (s: NoteSnippet) => void;
  onDelete: (s: NoteSnippet) => void;
  onSubmitToStore: (s: NoteSnippet) => void;
}) {
  const canEdit =
    snippet.level === 'personal'
      ? snippet.createdBy === currentUserId
      : isManager(userRole);

  const canSubmit =
    snippet.level === 'personal' &&
    snippet.createdBy === currentUserId &&
    !snippet.submittedForStore &&
    snippet.submissionStatus !== 'approved';

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="rounded-md bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-700">
              /{snippet.triggerCode}
            </span>
            {snippet.category ? (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                {snippet.category}
              </span>
            ) : null}
            {snippet.submittedForStore ? (
              <span
                className={[
                  'rounded-full px-2 py-0.5 text-xs font-semibold',
                  snippet.submissionStatus === 'approved'
                    ? 'bg-green-100 text-green-700'
                    : snippet.submissionStatus === 'rejected'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-amber-100 text-amber-700',
                ].join(' ')}
              >
                {snippet.submissionStatus === 'approved'
                  ? '✓ Approved'
                  : snippet.submissionStatus === 'rejected'
                    ? '✗ Rejected'
                    : '⏳ Pending review'}
              </span>
            ) : null}
          </div>
          <p className="font-semibold text-slate-950">{snippet.title}</p>
          <p className="line-clamp-2 text-sm text-slate-500">{snippet.content}</p>
          {snippet.rejectionReason ? (
            <p className="text-xs text-red-600">Reason: {snippet.rejectionReason}</p>
          ) : null}
          <p className="text-xs text-slate-400">Used {snippet.useCount} time{snippet.useCount !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex shrink-0 gap-1">
          {canSubmit ? (
            <button
              type="button"
              title="Submit to store"
              onClick={() => onSubmitToStore(snippet)}
              className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-primary"
            >
              <Send className="h-4 w-4" />
            </button>
          ) : null}
          {canEdit ? (
            <>
              <button
                type="button"
                title="Edit"
                onClick={() => onEdit(snippet)}
                className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-primary"
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                type="button"
                title="Delete"
                onClick={() => onDelete(snippet)}
                className="rounded-lg p-2 text-slate-500 hover:bg-red-50 hover:text-red-600"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function PendingReviewSection() {
  const queryClient = useQueryClient();
  const pendingQuery = useQuery({
    queryKey: ['snippets-pending-review'],
    queryFn: () => pendingReview(),
  });
  const [rejectionReason, setRejectionReason] = useState<Record<string, string>>({});

  const reviewMutation = useMutation({
    mutationFn: ({
      id,
      action,
      reason,
    }: {
      id: string;
      action: 'approve' | 'reject';
      reason?: string;
    }) => reviewSnippet(id, { action, rejectionReason: reason }),
    onSuccess: (_, vars) => {
      void queryClient.invalidateQueries({ queryKey: ['snippets-pending-review'] });
      void queryClient.invalidateQueries({ queryKey: ['snippets'] });
      toastSuccess(vars.action === 'approve' ? 'Snippet approved and added to store library' : 'Snippet rejected');
    },
    onError: (err) => {
      const p = parseApiError(err);
      toastError(p.message, p.code);
    },
  });

  const pending = pendingQuery.data ?? [];

  if (pendingQuery.isLoading) {
    return <p className="text-sm text-slate-500">Loading pending reviews…</p>;
  }

  if (!pending.length) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-500">
        No snippets pending review.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {pending.map((s: SnippetWithSubmitter) => (
        <div
          key={s.id}
          className="rounded-2xl border border-amber-200 bg-amber-50 p-4"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <p className="text-xs text-slate-500">Submitted by {s.submitterName}</p>
              <div className="flex items-center gap-2">
                <span className="rounded-md bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-700">
                  /{s.triggerCode}
                </span>
                {s.category ? (
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                    {s.category}
                  </span>
                ) : null}
              </div>
              <p className="font-semibold text-slate-950">{s.title}</p>
              <p className="whitespace-pre-wrap text-sm text-slate-700">{s.content}</p>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input
              className="flex-1 rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary"
              placeholder="Rejection reason (optional)"
              value={rejectionReason[s.id] ?? ''}
              onChange={(e) =>
                setRejectionReason((r) => ({ ...r, [s.id]: e.target.value }))
              }
            />
            <button
              type="button"
              disabled={reviewMutation.isPending}
              onClick={() =>
                reviewMutation.mutate({ id: s.id, action: 'approve' })
              }
              className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              <Check className="h-4 w-4" /> Approve
            </button>
            <button
              type="button"
              disabled={reviewMutation.isPending}
              onClick={() =>
                reviewMutation.mutate({
                  id: s.id,
                  action: 'reject',
                  reason: rejectionReason[s.id],
                })
              }
              className="inline-flex items-center gap-1.5 rounded-xl bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
            >
              <X className="h-4 w-4" /> Reject
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function SnippetsSettingsPage() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const userIsManager = isManager(user?.role);

  const [tab, setTab] = useState<Tab>('my');
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<NoteSnippet | null>(null);

  const snippetsQuery = useQuery({
    queryKey: ['snippets', tab],
    queryFn: () => listSnippets({ level: tab === 'my' ? 'personal' : 'store' }),
  });

  const createMutation = useMutation({
    mutationFn: (values: CreateSnippetBody) => createSnippet(values),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['snippets'] });
      setShowForm(false);
      toastSuccess('Snippet created');
    },
    onError: (err) => {
      const p = parseApiError(err);
      toastError(p.message, p.code);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, values }: { id: string; values: FormValues }) =>
      updateSnippet(id, {
        triggerCode: values.triggerCode,
        title: values.title,
        content: values.content,
        category: values.category || null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['snippets'] });
      setEditTarget(null);
      toastSuccess('Snippet updated');
    },
    onError: (err) => {
      const p = parseApiError(err);
      toastError(p.message, p.code);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => removeSnippet(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['snippets'] });
      toastSuccess('Snippet deleted');
    },
    onError: (err) => {
      const p = parseApiError(err);
      toastError(p.message, p.code);
    },
  });

  const submitMutation = useMutation({
    mutationFn: (id: string) => submitToStore(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['snippets'] });
      toastSuccess('Snippet submitted for store review');
    },
    onError: (err) => {
      const p = parseApiError(err);
      toastError(p.message, p.code);
    },
  });

  function handleDelete(snippet: NoteSnippet) {
    if (!window.confirm(`Delete snippet "/${snippet.triggerCode}"?`)) return;
    deleteMutation.mutate(snippet.id);
  }

  const snippets = snippetsQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-slate-950">Note Snippets</h2>
            <p className="mt-1 text-sm text-slate-500">
              Type <span className="font-mono">/trigger</span> in any note or message to quickly
              insert a snippet.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setEditTarget(null);
              setShowForm((v) => !v);
            }}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-[#164477]"
          >
            <Plus className="h-4 w-4" />
            New Snippet
          </button>
        </div>

        {showForm && !editTarget ? (
          <div className="mt-4">
            <SnippetForm
              allowStore={userIsManager}
              saving={createMutation.isPending}
              onCancel={() => setShowForm(false)}
              onSave={(values) =>
                createMutation.mutate({
                  triggerCode: values.triggerCode,
                  title: values.title,
                  content: values.content,
                  category: values.category || undefined,
                  level: values.level,
                })
              }
            />
          </div>
        ) : null}

        <div className="mt-5 flex gap-2 border-b border-slate-200 pb-0">
          {(['my', 'store'] as Tab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={[
                '-mb-px border-b-2 pb-2 px-3 text-sm font-semibold transition-colors',
                tab === t
                  ? 'border-primary text-primary'
                  : 'border-transparent text-slate-500 hover:text-slate-800',
              ].join(' ')}
            >
              {t === 'my' ? 'My Snippets' : 'Store Snippets'}
            </button>
          ))}
        </div>

        <div className="mt-4 space-y-3">
          {snippetsQuery.isLoading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : snippets.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
              No {tab === 'my' ? 'personal' : 'store'} snippets yet. Create one above.
            </div>
          ) : (
            snippets.map((s) =>
              editTarget?.id === s.id ? (
                <SnippetForm
                  key={s.id}
                  allowStore={userIsManager}
                  saving={updateMutation.isPending}
                  initial={{
                    triggerCode: s.triggerCode,
                    title: s.title,
                    content: s.content,
                    category: s.category ?? '',
                    level: s.level,
                  }}
                  onCancel={() => setEditTarget(null)}
                  onSave={(values) => updateMutation.mutate({ id: s.id, values })}
                />
              ) : (
                <SnippetCard
                  key={s.id}
                  snippet={s}
                  currentUserId={user?.id}
                  userRole={user?.role}
                  onEdit={setEditTarget}
                  onDelete={handleDelete}
                  onSubmitToStore={(sn) => submitMutation.mutate(sn.id)}
                />
              ),
            )
          )}
        </div>
      </div>

      {userIsManager ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-lg font-bold text-slate-950">Pending Review</h3>
          <p className="mt-1 text-sm text-slate-500">
            Staff have submitted these personal snippets to the store library for your approval.
          </p>
          <div className="mt-4">
            <PendingReviewSection />
          </div>
        </div>
      ) : null}
    </div>
  );
}
